/**
 * An accidental close must not destroy what somebody typed. (BETA-00220)
 *
 * > "please make all modal form content persistant and only clear on save or
 * >  clear. accidental closes are causing frustration across the whole
 * >  estate." — Callum, 2026-08-07
 *
 * TWO ways a modal closes, and they are not the same act:
 *
 *   DELIBERATE — the ✕, a Cancel button, an action button. The person has
 *                said they want out. Unchanged by this file.
 *   ACCIDENTAL — Escape, or a click on the backdrop. Nobody means these.
 *                A stray click beside a dialog should not bin ten minutes
 *                of typing, and on a laptop trackpad it constantly does.
 *
 * 🔴 **Why this is one global guard and not a change to `showModal`.**
 * Measured 2026-08-10: **20 files use `showModal`; 33 hand-roll their own
 * `position:fixed` overlay** with the near-universal
 * `if (e.target === overlay) close()`. Fixing the shared component would
 * therefore have covered under half the estate, and the half it missed
 * includes the big authoring forms this was reported about. So the guard
 * hooks the two *events*, in the CAPTURE phase, and is blind to which modal
 * implementation is underneath.
 *
 * ⚠️ **Capture phase is load-bearing.** `showModal` listens for Escape on
 * `document` in the bubble phase, and hand-rolled overlays listen for clicks
 * on themselves. A capture listener on `document` runs before both, so
 * `stopPropagation()` here means the event never reaches either — no
 * per-component cooperation needed, and no component can opt out by accident.
 *
 * ⚠️ **The escape hatch is deliberate and must stay.** A modal with no ✕ and
 * no Cancel would otherwise be a trap once dirty. A SECOND Escape within
 * `FORCE_WINDOW_MS` closes regardless — impossible to hit by accident,
 * always available when someone genuinely wants out.
 */
import { t } from '../i18n/index.js';

const dirty = new WeakSet();     // overlays the user has typed into
const armed = new WeakMap();     // overlay -> timestamp of the blocked Escape

const FORCE_WINDOW_MS = 4000;

// ⚠️ Search and filter boxes are NOT work. Typing in one to narrow a picker
// then pressing Escape should still close — guarding those turns a
// convenience into an obstacle, which is how a protective feature ends up
// resented. Opt a field out with `data-no-dirty-guard` for the same reason.
const IGNORED = (el) => (
  el.type === 'search'
  || el.getAttribute?.('role') === 'searchbox'
  || el.hasAttribute?.('data-no-dirty-guard')
  || el.closest?.('[data-no-dirty-guard]') != null
);

let _installed = false;

/**
 * Install the guard. Idempotent; only the first call registers listeners.
 */
export function initDirtyGuard() {
  if (_installed || typeof document === 'undefined') return;
  _installed = true;

  // Anything the user types, ticks or picks marks that overlay as holding
  // work. `input` covers contenteditable too, which is what TipTap uses —
  // comparing values would have missed the rich-text editors entirely, and
  // those hold the longest-typed content in the estate.
  document.addEventListener('input', onEdit, true);
  document.addEventListener('change', onEdit, true);

  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('click', onClick, true);
}

function onEdit(e) {
  const el = e.target;
  if (!el || IGNORED(el)) return;
  const overlay = overlayOf(el);
  if (overlay) dirty.add(overlay);
}

/** The modal backdrop containing this element, if any. */
function overlayOf(el) {
  return el.closest?.(
    '.modal-overlay, .doc-modal-overlay, [role="dialog"][aria-modal="true"]',
  ) || fixedAncestor(el);
}

/**
 * Hand-rolled overlays carry no agreed class, so fall back to structure:
 * the nearest ancestor that is fixed/absolute and covers the viewport.
 * ⚠️ Same 85% test the Esc-dismiss handler uses — keep them consistent, or
 * a layer can be "an overlay" to one and not the other.
 */
function fixedAncestor(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const cs = getComputedStyle(node);
    if (cs.position === 'fixed' || cs.position === 'absolute') {
      const r = node.getBoundingClientRect();
      if (r.width >= window.innerWidth * 0.85
          && r.height >= window.innerHeight * 0.85) return node;
    }
    node = node.parentElement;
  }
  return null;
}

function onKeydown(e) {
  if (e.key !== 'Escape' && e.keyCode !== 27) return;
  // Find the overlay the focus is in; fall back to whatever is on top.
  const overlay = overlayOf(document.activeElement) || topOverlay();
  if (!overlay || !dirty.has(overlay)) return;

  const since = armed.get(overlay);
  if (since && Date.now() - since < FORCE_WINDOW_MS) {
    // Second Escape — they mean it. Let it through untouched.
    armed.delete(overlay);
    dirty.delete(overlay);
    return;
  }

  e.preventDefault();
  e.stopPropagation();
  armed.set(overlay, Date.now());
  hint(overlay, t('dirtyGuard.escape_again'));
}

function onClick(e) {
  // Only a click on the backdrop ITSELF closes a modal. A click on the ✕,
  // on Cancel, or on anything inside the dialog is deliberate and is never
  // touched by this guard.
  const overlay = e.target;
  if (!(overlay instanceof HTMLElement) || !dirty.has(overlay)) return;
  e.preventDefault();
  e.stopPropagation();
  hint(overlay, t('dirtyGuard.use_cancel'));
}

function topOverlay() {
  const els = Array.from(document.body.children).filter((el) => {
    if (!(el instanceof HTMLElement) || !dirty.has(el)) return false;
    const cs = getComputedStyle(el);
    return (cs.position === 'fixed' || cs.position === 'absolute')
      && cs.display !== 'none' && cs.visibility !== 'hidden';
  });
  return els[els.length - 1] || null;
}

/**
 * A transient line saying what just happened.
 *
 * ⚠️ Silently refusing to close would read as the app being broken — worse
 * than losing the content, because at least losing it is comprehensible.
 * Blocking an action ALWAYS has to say why.
 *
 * 🔴 **Fixed to the VIEWPORT and appended to `<body>`, not to the dialog.**
 * The first version sat at the modal card's top edge, and on a tall modal
 * that edge is at y≈45px — so the pill was sliced in half by the top of the
 * screen. The browser test passed (the text was in the DOM and correct);
 * only the SCREENSHOT showed it. Appending to body also survives a modal
 * with a transformed ancestor, which would otherwise re-root `fixed` to
 * that ancestor and reintroduce the clipping.
 *
 * ⚠️ Colours are LITERAL, not tokens. The ui-kit grey scale inverts under
 * `[data-theme="dark"]`, so a token pair here is unreadable in exactly one
 * theme — the same trap the org-chart tooltip and the RSVP chips hit.
 */
let _hintEl = null;
let _hintTimer = null;

function hint(_overlay, message) {
  if (!_hintEl || !_hintEl.isConnected) {
    _hintEl = document.createElement('div');
    _hintEl.className = 'isg-dirty-hint';
    _hintEl.setAttribute('role', 'status');
    _hintEl.style.cssText = [
      'position:fixed', 'left:50%', 'top:16px', 'transform:translateX(-50%)',
      // Above every modal in the estate — the highest z-index in use is the
      // 1100 an events sub-modal claims, and a warning about a modal must
      // never render behind it.
      'z-index:2147482000',
      'max-width:min(92vw,520px)', 'box-sizing:border-box',
      'padding:10px 16px', 'border-radius:999px',
      'background:#171e26', 'color:#f1f2f4',
      'font-size:13px', 'line-height:1.4', 'font-weight:500',
      'box-shadow:0 6px 20px rgba(0,0,0,.35)', 'pointer-events:none', // i18n-ignore: CSS, not text
      'text-align:center', 'transition:opacity .25s', 'opacity:0',
    ].join(';');
    document.body.appendChild(_hintEl);
  }
  _hintEl.textContent = message;
  requestAnimationFrame(() => { if (_hintEl) _hintEl.style.opacity = '1'; });
  clearTimeout(_hintTimer);
  _hintTimer = setTimeout(() => {
    if (!_hintEl) return;
    _hintEl.style.opacity = '0';
    // Remove rather than leave a permanently-invisible node on <body>;
    // an SPA that opens fifty modals would otherwise accumulate fifty.
    setTimeout(() => { _hintEl?.remove(); _hintEl = null; }, 300);
  }, 3200);
}

/**
 * Forget a modal's dirty state — call after a successful SAVE, so the
 * component can close itself without the guard arguing.
 *
 * ⚠️ Usually unnecessary: a saved modal is removed from the DOM and the
 * WeakSet entry goes with it. This exists for modals that stay open after
 * saving (a wizard step, an inline "saved" state).
 */
export function clearDirty(elementInsideModal) {
  const overlay = overlayOf(elementInsideModal);
  if (overlay) { dirty.delete(overlay); armed.delete(overlay); }
}

/** Whether a modal currently holds unsaved input. Exposed for tests. */
export function isDirty(elementInsideModal) {
  const overlay = overlayOf(elementInsideModal);
  return !!overlay && dirty.has(overlay);
}
